import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bot, Zap, Search, PhoneCall } from "lucide-react";

const SOURCE = {
  owner: "Finds 10 new for-lease-by-owner or expiring rental listing owners a day, fills in contact info and DNC-scrubs them.",
  agent: "Finds 10 new real estate agents and brokers a day (rental listing agents + Florida DBPR), fills in contact info and DNC-scrubs them.",
  fsbo: "Finds 10 new for-sale-by-owner homes a day (RentCast sale listings with no listing agent), looks up the owner, fills in contact info and DNC-scrubs them.",
  expired: "Finds 10 new listings a day that came off the market without selling (RentCast), looks up the owner, fills in contact info and DNC-scrubs them. Expired and cancelled listings look the same in RentCast; import MLS exports with Import CSV.",
};
const ROWS = (kind) => [
  ["source", "Daily sourcing", SOURCE[kind]],
  ["email", "Weekly email + postcards", "One email a week to every lead (with an opt-out). A one-time postcard only when a lead has no email or the email bounced."],
  ["dial", "Calls, texts & voicemails", "Daily until the lead says they're not interested, hangs up within 30 seconds, or is on the DNC list. Mon-Sat, 9 AM-6 PM ET."],
];
const MODES = [["zips", "ZIP codes"], ["cities", "Cities"], ["counties", "Counties"], ["statewide", "All of Florida"]];
const HINT = { zips: "32714, 32801", cities: "Orlando, Winter Park, Tampa", counties: "Orange, Seminole, Hillsborough" };

const AreaPicker = ({ cfg, save }) => {
  const [text, setText] = useState("");
  useEffect(() => { setText(Array.isArray(cfg[cfg.mode]) ? cfg[cfg.mode].join(", ") : ""); }, [cfg]);
  return (
    <div className="mb-4">
      <div className="mb-2 text-xs font-semibold text-slate-600">Where to search</div>
      <div className="mb-2 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1 sm:grid-cols-4" data-testid="pm-area-mode">
        {MODES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => save({ mode: k })} data-testid={`pm-area-${k}`}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${cfg.mode === k ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {cfg.mode !== "statewide" && (
        <div className="flex flex-wrap items-end gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={HINT[cfg.mode]} className="min-w-[220px] flex-1" data-testid="pm-area-input" />
          <Button variant="outline" onClick={() => save({ [cfg.mode]: text.split(cfg.mode === "zips" ? /[\s,]+/ : /,/).map((s) => s.trim()).filter(Boolean) })} data-testid="pm-area-save">Save</Button>
        </div>
      )}
    </div>
  );
};

export const TcpaDialog = ({ open, onClose, text, onSign, title }) => {
  const [name, setName] = useState("");
  useEffect(() => { if (!open) setName(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pm-tcpa-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-700">{text}</p>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full legal name to sign" data-testid="pm-tcpa-name" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="pm-tcpa-cancel">Cancel</Button>
          <Button disabled={name.trim().length < 3} onClick={() => onSign(name)} data-testid="pm-tcpa-sign">Sign &amp; continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PmAutopilot = ({ kind }) => {
  const [cfg, setCfg] = useState(null);
  const [tcpa, setTcpa] = useState(null);
  useEffect(() => { api.get("/pm/autopilot").then((r) => setCfg(r.data)); }, []);
  const save = async (patch) => {
    try { const r = await api.put("/pm/autopilot", { kind, ...patch }); setCfg(r.data); toast.success("Saved"); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  const post = async (path, body, msg) => {
    try { const { data } = await api.post(path, { kind, ...body }); toast.success(msg(data)); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  const sign = async (name) => {
    const ok = tcpa === "dial" ? await save({ dial: true, tcpa_agree: true, tcpa_name: name })
      : await post("/pm/dial-now", { tcpa_agree: true, tcpa_name: name }, (d) => `Calling, texting and leaving voicemails for ${d.count} leads now`);
    if (ok) setTcpa(null);
  };
  if (!cfg) return null;
  const ap = cfg.autopilot[kind];
  const callsOff = cfg.dialing_disabled || cfg.dial_now_disabled;
  return (
    <Card className="mb-5 p-6" data-testid={`pm-autopilot-${kind}`}>
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Bot className="h-5 w-5" />AI autopilots</h3>
      <p className="mb-4 text-sm text-slate-600">Turn each one on or off at any time.</p>
      <AreaPicker cfg={cfg} save={save} />
      {kind === "owner" && cfg.rentcast_error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="pm-rentcast-error">
          Property owner sourcing is paused: the rental listing service (RentCast) is rejecting our account — "{cfg.rentcast_error}". The brokerage needs to reactivate the RentCast subscription; Elite Rentals sourcing still works.
        </div>
      )}
      <div className="mb-4 flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => post("/pm/source-now", {}, () => "Sourcing 10 leads now - check back in a few minutes")} data-testid={`pm-source-now-${kind}`}><Search className="mr-2 h-4 w-4" />Source 10 leads now</Button>
        <Button className="gold-cta border-0 font-bold" onClick={() => post("/pm/run-now", {}, () => "Emailing and mailing your leads now - check back in a few minutes")} data-testid={`pm-run-now-${kind}`}><Zap className="mr-2 h-4 w-4" />Email &amp; mail now</Button>
        <Button variant="outline" disabled={callsOff} onClick={() => setTcpa("now")} data-testid={`pm-dial-now-${kind}`} title={callsOff ? "Turned off by the administrator" : ""}><PhoneCall className="mr-2 h-4 w-4" />Call, text &amp; voicemail all now</Button>
      </div>
      {callsOff && <div className="mb-3 text-xs font-semibold text-red-700" data-testid="pm-dial-now-off-note">Calling all leads now is turned off by the administrator.</div>}
      <div className="space-y-3">
        {ROWS(kind).map(([k, label, desc]) => (
          <div key={k} className="flex items-start justify-between gap-4 border-t border-slate-100 pt-3">
            <div><div className="font-semibold text-slate-900">{label}</div><div className="text-xs text-slate-500">{desc}</div>
              {k === "dial" && cfg.dialing_disabled && <div className="mt-1 text-xs font-semibold text-red-700" data-testid="pm-dial-disabled-note">Turned off for all property managers by the administrator.</div>}</div>
            <Switch checked={!!ap[k]} disabled={k === "dial" && cfg.dialing_disabled && !ap[k]} onCheckedChange={(v) => (k === "dial" && v ? setTcpa("dial") : save({ [k]: v }))} data-testid={`pm-ap-${kind}-${k}`} />
          </div>
        ))}
      </div>
      <TcpaDialog open={!!tcpa} onClose={() => setTcpa(null)} text={cfg.tcpa_text} onSign={sign} title="Permission to contact" />
    </Card>
  );
};
